(() => {
  if (customElements.get('trail-crew-builder')) return;
  const el = (tag, text, className) => {
    const node = document.createElement(tag);
    if (text !== undefined) node.textContent = text;
    if (className) node.className = className;
    return node;
  };
  function cartItems(rows) {
    const items = new Map();
    for (const row of rows) {
      const quantity = Number(row.quantity.value);
      if (!row.variant?.available || !Number.isSafeInteger(quantity) || quantity < 1 || quantity > 99) {
        throw new Error('Choose an available shirt and a whole quantity from 1 to 99 for every crew member.');
      }
      const id = row.variant.id;
      const total = (items.get(id)?.quantity || 0) + quantity;
      if (total > 99) throw new Error('Please limit each shirt combination to 99 per order.');
      items.set(id, { id, quantity: total });
    }
    if (!items.size) throw new Error('Add a crew member first.');
    return [...items.values()];
  }
  class TrailCrewBuilder extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.products = JSON.parse(this.querySelector('[data-products]').textContent).filter(p => p.variants.length);
      this.rows = [];
      this.busy = false;
      this.duplicateApproved = null;
      this.root = this.dataset.root || '/';
      if (!this.root.endsWith('/')) this.root += '/';
      this.status = this.querySelector('[data-status]');
      this.submit = this.querySelector('[data-submit]');
      this.money = new Intl.NumberFormat(this.dataset.locale || 'en', { style: 'currency', currency: this.dataset.currency || 'USD' });
      if (!this.products.length) {
        this.status.textContent = 'No shirts are available here yet. Please use the individual shirt links below.';
        return;
      }
      this.querySelector('[data-controls]').hidden = false;
      this.querySelector('[data-another]').addEventListener('click', () => this.addRow(true));
      this.submit.addEventListener('click', () => this.addToCart());
      this.duplicateNotice = this.querySelector('[data-duplicate-warning]');
      this.querySelector('[data-skip-primary]')?.addEventListener('click', () => {
        this.primaryToggle.checked = false;
        this.duplicateApproved = null;
        this.duplicateNotice.hidden = true;
        this.status.textContent = 'Your first shirt stays in your cart. Add your trail buddies when you are ready.';
        this.update();
      });
      this.querySelector('[data-confirm-primary]')?.addEventListener('click', () => {
        this.duplicateApproved = this.pendingDuplicate;
        this.duplicateNotice.hidden = true;
        this.status.textContent = 'Another of your first shirt will be included. Click Add crew to cart to continue.';
      });
      this.primaryToggle = this.querySelector('[data-include-primary]');
      if (this.primaryToggle) {
        this.primaryToggle.addEventListener('change', () => this.update());
        this.onProductChange = event => {
          if (!this.contains(event.target)) this.update();
        };
        document.addEventListener('change', this.onProductChange);
        document.addEventListener('input', this.onProductChange);
        const productArea = document.querySelector('[id^="MainProduct-"]');
        if (productArea) {
          this.productObserver = new MutationObserver(mutations => {
            const external = mutations.some(mutation => {
              if (this.contains(mutation.target)) return false;
              const nodes = [...mutation.addedNodes, ...mutation.removedNodes];
              if (mutation.type === 'childList' && nodes.length && nodes.every(node => node === this.layoutWrapper)) return false;
              return true;
            });
            if (external) this.update();
          });
          this.productObserver.observe(productArea, { subtree: true, childList: true, attributes: true, attributeFilter: ['value', 'disabled', 'checked'] });
        }
        this.update();
      } else {
        this.addRow();
      }
      this.setupCrewJumpLink();
      this.setupMobilePlacement();
    }
    setupCrewJumpLink() {
      if (!this.dataset.currentProduct) return;
      const button = document.querySelector('[id^="MainProduct-"] [id^="ProductSubmitButton-"]');
      const purchaseBlock = button?.closest('product-form');
      if (!purchaseBlock) return;
      if (!this.id) this.id = this.closest('.shopify-section').id + '-builder';
      this.jumpLink = el('a', 'Build a crew pack', 'tcb-jump');
      this.jumpLink.href = '#' + this.id;
      this.jumpLink.addEventListener('click', event => {
        event.preventDefault();
        this.scrollIntoView({ block: 'start', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
        const heading = this.querySelector('h2');
        heading?.setAttribute('tabindex', '-1');
        heading?.focus({ preventScroll: true });
      });
      purchaseBlock.parentNode.insertBefore(this.jumpLink, purchaseBlock);
    }
    setupMobilePlacement() {
      if (!this.dataset.currentProduct) return;
      if (!this.closest('.shopify-section')) return;
      this.layoutWrapper = this;
      this.layoutAnchor = document.createComment('Trail Crew desktop position');
      this.layoutWrapper.parentNode.insertBefore(this.layoutAnchor, this.layoutWrapper);
      this.mobileQuery = window.matchMedia('(max-width: 749px)');
      this.moveForViewport = () => {
        if (!this.isConnected || !this.layoutAnchor.isConnected) return;
        const nativeProduct = document.querySelector('[id^="MainProduct-"]');
        const button = nativeProduct?.querySelector('[id^="ProductSubmitButton-"]');
        const purchaseBlock = button?.closest('product-form');
        const target = this.mobileQuery.matches && purchaseBlock ? purchaseBlock : this.layoutAnchor;
        if (target.nextElementSibling === this.layoutWrapper) return;
        this.relocating = true;
        target.parentNode.insertBefore(this.layoutWrapper, target.nextSibling);
        this.relocating = false;
      };
      this.mobileQuery.addEventListener('change', this.moveForViewport);
      this.moveForViewport();
    }
    disconnectedCallback() {
      if (this.relocating) return;
      this.mobileQuery?.removeEventListener('change', this.moveForViewport);
      this.layoutAnchor?.remove();
      this.jumpLink?.remove();
      document.removeEventListener('change', this.onProductChange);
      document.removeEventListener('input', this.onProductChange);
      this.productObserver?.disconnect();
      this.ready = false;
    }
    primarySelection() {
      if (!this.primaryToggle?.checked) return null;
      const area = document.querySelector('[id^="MainProduct-"]');
      const form = Array.from((area || document).querySelectorAll('form[action*="/cart/add"]'))
        .find(candidate => candidate.querySelector('[name="add"]') && candidate.querySelector('[name="id"]'));
      if (!form) throw new Error('The shirt selected above could not be read. Uncheck its inclusion to order only the extra shirts.');
      const fields = new FormData(form);
      const id = Number(fields.get('id'));
      const product = this.products.find(candidate => String(candidate.id) === this.dataset.currentProduct);
      const variant = product?.variants.find(candidate => candidate.id === id);
      if (!variant?.available || form.querySelector('[name="add"]')?.disabled) throw new Error('Choose an available size and color for the shirt above.');
      return { product, variant, quantity: { value: fields.get('quantity') || '1' } };
    }
    selections() {
      const primary = this.primarySelection();
      return primary ? [primary, ...this.rows] : this.rows;
    }
    setBusy(busy) {
      this.busy = busy;
      this.rows.forEach(row => { row.fieldset.disabled = busy; });
      this.querySelector('[data-another]').disabled = busy;
      if (this.primaryToggle) this.primaryToggle.disabled = busy;
      this.update();
    }
    field(container, title, input, className) {
      const label = el('label', undefined, className);
      label.append(el('span', title), input);
      container.append(label);
      return input;
    }
    addRow(focus = false) {
      if (this.busy) return;
      const box = el('div', undefined, 'tcb-row');
      const image = el('img'); image.alt = ''; image.loading = 'lazy';
      const fieldset = el('fieldset');
      const legend = el('legend');
      const fields = el('div', undefined, 'tcb-fields');
      const saying = el('select');
      this.products.forEach((p, i) => saying.add(new Option(p.label, String(i))));
      this.field(fields, 'Saying', saying, 'tcb-saying');
      const options = el('div', undefined, 'tcb-fields'); options.style.display = 'contents';
      fields.append(options);
      const quantity = el('input'); quantity.type = 'number'; quantity.min = '1'; quantity.max = '99'; quantity.step = '1'; quantity.value = '1'; quantity.inputMode = 'numeric';
      this.field(fields, 'Quantity', quantity, 'tcb-quantity');
      const price = el('p', undefined, 'tcb-row-price');
      const remove = el('button', 'Remove', 'tcb-secondary tcb-remove'); remove.type = 'button';
      fieldset.append(legend, fields, price, remove); box.append(image, fieldset);
      const row = { box, image, legend, saying, options, quantity, price, fieldset, variant: null, selectors: [] };
      saying.addEventListener('change', () => this.chooseProduct(row));
      quantity.addEventListener('input', () => this.update());
      remove.addEventListener('click', () => {
        if (this.busy) return;
        const index = this.rows.indexOf(row);
        this.rows.splice(index, 1); box.remove(); this.update();
        (this.rows[Math.min(index, this.rows.length - 1)]?.saying || this.querySelector('[data-another]')).focus();
      });
      this.rows.push(row); this.querySelector('[data-rows]').append(box);
      this.chooseProduct(row);
      if (focus) saying.focus();
    }
    chooseProduct(row) {
      row.product = this.products[Number(row.saying.value)];
      row.options.replaceChildren(); row.selectors = [];
      const initial = row.product.variants.find(v => v.available) || row.product.variants[0];
      row.product.options.forEach((name, index) => {
        const select = el('select');
        [...new Set(row.product.variants.map(v => v.options[index]))].forEach(value => select.add(new Option(value, value)));
        select.value = initial.options[index];
        select.addEventListener('change', () => this.resolve(row));
        this.field(row.options, name, select);
        row.selectors.push(select);
      });
      this.resolve(row);
    }
    resolve(row) {
      const selected = row.selectors.map(s => s.value);
      row.variant = row.product.variants.find(v => v.options.every((value, i) => value === selected[i])) || null;
      // Leave every option selectable: customers can change color even if the current size is sold out.
      const source = row.variant?.image || row.product.image;
      row.image.hidden = !source;
      if (source) row.image.src = source;
      row.image.alt = row.product.label + ' — ' + selected.join(' / ');
      row.price.textContent = row.variant?.available ? this.money.format(row.variant.price / 100) + '+' : 'This combination is unavailable. Choose another size or color.';
      this.update();
    }
    update() {
      this.rows.forEach((row, i) => { row.legend.textContent = 'Trail buddy ' + (i + 1); });
      try {
        const selections = this.selections();
        const primary = this.primarySelection();
        const primarySummary = this.querySelector('[data-primary-summary]');
        if (primarySummary) primarySummary.textContent = primary ? primary.product.title + ' — ' + primary.variant.options.join(' / ') + ' × ' + primary.quantity.value : 'Only the extra shirts below will be added.';
        const items = cartItems(selections);
        const total = selections.reduce((sum, row) => sum + row.variant.price * Number(row.quantity.value), 0);
        const count = items.reduce((sum, item) => sum + item.quantity, 0);
        this.querySelector('[data-summary]').textContent = count + (count === 1 ? ' shirt · ' : ' shirts · ') + this.money.format(total / 100);
        this.submit.disabled = this.busy;
      } catch (error) {
        this.querySelector('[data-summary]').textContent = error.message;
        this.submit.disabled = true;
      }
    }
    async checkPrimaryDuplicate(primary) {
      if (!primary) return false;
      const response = await fetch(this.root + 'cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!response.ok) throw new Error('We could not check your cart. Please try again.');
      const cart = await response.json();
      if (!Array.isArray(cart.items)) throw new Error('We could not read your cart. Please try again.');
      const cartQuantity = cart.items.filter(item => Number(item.variant_id || item.id) === primary.variant.id)
        .reduce((sum, item) => sum + Number(item.quantity || 0), 0);
      if (!cartQuantity) return false;
      const selected = { id: primary.variant.id, cartQuantity, quantity: Number(primary.quantity.value) };
      if (this.duplicateApproved && Object.keys(selected).every(key => this.duplicateApproved[key] === selected[key])) return false;
      this.pendingDuplicate = selected;
      this.querySelector('[data-duplicate-message]').textContent = primary.product.title + ' (' + primary.variant.options.join(' / ') + ') is already in your cart. Keep it there and add only your trail buddies, or choose to add another.';
      this.duplicateNotice.hidden = false;
      this.status.textContent = 'Your first shirt is already in the cart. Choose how to continue above.';
      this.duplicateNotice.scrollIntoView({ block: 'center', behavior: 'smooth' });
      return true;
    }
    async addToCart() {
      if (this.busy) return;
      let items;
      try { items = cartItems(this.selections()); } catch (error) { this.status.textContent = error.message; return; }
      this.setBusy(true);
      this.status.textContent = 'Checking your crew…';
      let addAttempted = false;
      try {
        if (await this.checkPrimaryDuplicate(this.primarySelection())) {
          this.setBusy(false);
          return;
        }
        this.duplicateNotice.hidden = true;
        this.status.textContent = 'Adding your crew…';
        addAttempted = true;
        const response = await fetch(this.root + 'cart/add.js', {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items })
        });
        const data = await response.json();
        if (!response.ok || data.status >= 400) throw new Error(typeof data.description === 'string' ? data.description : 'Shopify could not add every shirt.');
        this.status.textContent = 'Your crew was added.';
        this.duplicateApproved = null;
        const cartLink = this.querySelector('[data-cart]');
        cartLink.hidden = false;
        cartLink.textContent = 'View cart';
        try {
          if (typeof window.upcartRefreshCart === 'function' && typeof window.upcartOpenCart === 'function') {
            await window.upcartRefreshCart();
            window.upcartOpenCart();
          } else {
            const cartButton = document.getElementById('cart-icon-bubble');
            if (cartButton) cartButton.click();
            else window.location.assign(this.root + 'cart');
          }
        } catch (_) {
          this.status.textContent = 'Your shirts were added, but the cart drawer could not refresh. Open the cart before adding again.';
        } finally {
          this.setBusy(false);
        }
      } catch (error) {
        // A timeout or stock error can follow a partial cart update. Never automatically retry.
        this.status.textContent = (error.message || 'The request could not be confirmed.') + (addAttempted ? ' Check your cart before adding again; some shirts may already have been added.' : ' Your crew order was not sent.');
        this.querySelector('[data-cart]').hidden = false;
        this.setBusy(false);
      }
    }
  }
  customElements.define('trail-crew-builder', TrailCrewBuilder);
})();
